import React from "react";
import {
  apiGetPengembalian, apiCatatPengembalian, apiKoreksiPengembalian, formatApiErrorDetail,
} from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Loader2, X, Pencil, Printer } from "lucide-react";
import { useCetak } from "@/hooks/usePengaturan";
import * as W from "@/lib/waktu";

// Owner only: settling the money of a cancelled, paid nota. Every action is a
// new entry; nothing is edited or deleted. The customer's bank account shown
// here is personal data and is only ever returned to the owner.
const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const lbl = "text-[13px] font-semibold text-slate-200";
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const num = (v) => parseInt(v, 10) || 0;
const JENIS = { PENGEMBALIAN: "Dikembalikan", TIDAK_DIKEMBALIKAN: "Tidak dikembalikan", KOREKSI: "Koreksi" };

function fmtTanggal(iso, tz) {
  if (!iso) return "—";
  return W.waktu(iso);
}

function KembaliForm({ notaId, saldo, rekening, onDone }) {
  const [f, setF] = React.useState({ tunai: "", transfer: "", rekening_toko_id: "", bank: "", nomor: "", atas_nama: "", keterangan: "" });
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const transfer = num(f.transfer);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await apiCatatPengembalian(notaId, {
        jenis: "PENGEMBALIAN", tunai: num(f.tunai), transfer,
        rekening_toko_id: transfer > 0 ? f.rekening_toko_id || null : null,
        rekening_customer: transfer > 0 ? { bank: f.bank, nomor: f.nomor, atas_nama: f.atas_nama } : null,
        keterangan: f.keterangan || null,
      });
      onDone();
    } catch (e2) { setError(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <form data-testid="kembali-form" onSubmit={submit} className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
      <div className="col-span-2 md:col-span-4 text-sm font-semibold text-emerald-300">Catat uang yang dikembalikan</div>
      <div><label className={lbl}>Tunai</label><input data-testid="kembali-tunai" type="number" min="0" max={saldo} value={f.tunai} onChange={set("tunai")} className={inputCls} /></div>
      <div><label className={lbl}>Transfer</label><input data-testid="kembali-transfer" type="number" min="0" max={saldo} value={f.transfer} onChange={set("transfer")} className={inputCls} /></div>
      <div className="col-span-2"><label className={lbl}>Keterangan (opsional)</label><input data-testid="kembali-keterangan" value={f.keterangan} onChange={set("keterangan")} maxLength={200} className={inputCls} /></div>
      {transfer > 0 && (<>
        <div className="col-span-2 md:col-span-4"><label className={lbl}>Dari rekening toko</label>
          <select data-testid="kembali-rekening-toko" value={f.rekening_toko_id} onChange={set("rekening_toko_id")} required className={inputCls}>
            <option value="">— pilih —</option>
            {(rekening || []).filter((r) => r.is_active).map((r) => <option key={r.id} value={r.id}>{r.bank} {r.nomor} a.n. {r.atas_nama}</option>)}
          </select></div>
        <div><label className={lbl}>Bank customer</label><input data-testid="kembali-bank" value={f.bank} onChange={set("bank")} required maxLength={50} className={inputCls} /></div>
        <div><label className={lbl}>No. rekening customer</label><input data-testid="kembali-nomor" value={f.nomor} onChange={set("nomor")} required minLength={3} maxLength={40} className={inputCls} /></div>
        <div className="col-span-2"><label className={lbl}>Atas nama</label><input data-testid="kembali-atas-nama" value={f.atas_nama} onChange={set("atas_nama")} required maxLength={100} className={inputCls} /></div>
        <p className="col-span-2 md:col-span-4 text-[11px] text-slate-500">Rekening customer adalah data pribadi: hanya Owner yang dapat melihatnya. Tidak tampil di nota, tracking, atau layar kasir.</p>
      </>)}
      <button data-testid="kembali-submit" type="submit" disabled={busy || num(f.tunai) + transfer <= 0} className="col-span-2 md:col-span-1 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">{busy ? "…" : "Simpan pengembalian"}</button>
      {error && <p data-testid="kembali-error" className="col-span-2 md:col-span-4 text-xs text-red-300">{error}</p>}
    </form>
  );
}

function TidakForm({ notaId, saldo, onDone }) {
  const [jumlah, setJumlah] = React.useState(String(saldo));
  const [alasan, setAlasan] = React.useState("");
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { await apiCatatPengembalian(notaId, { jenis: "TIDAK_DIKEMBALIKAN", jumlah: num(jumlah), alasan }); onDone(); }
    catch (e2) { setError(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <form data-testid="tidak-form" onSubmit={submit} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
      <div className="col-span-2 md:col-span-4 text-sm font-semibold text-amber-300">Catat yang tidak dikembalikan</div>
      <div><label className={lbl}>Jumlah</label><input data-testid="tidak-jumlah" type="number" min="1" max={saldo} value={jumlah} onChange={(e) => setJumlah(e.target.value)} className={inputCls} /></div>
      <div className="col-span-2"><label className={lbl}>Alasan (wajib)</label><input data-testid="tidak-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} required minLength={5} maxLength={300} className={inputCls} /></div>
      <button data-testid="tidak-submit" type="submit" disabled={busy || num(jumlah) <= 0 || alasan.trim().length < 5} className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">{busy ? "…" : "Simpan"}</button>
      {error && <p data-testid="tidak-error" className="col-span-2 md:col-span-4 text-xs text-red-300">{error}</p>}
    </form>
  );
}

function KoreksiForm({ entri, nilai, onDone }) {
  const kembali = entri.jenis === "PENGEMBALIAN";
  const [tunai, setTunai] = React.useState(String(nilai.tunai));
  const [transfer, setTransfer] = React.useState(String(nilai.transfer));
  const [jumlah, setJumlah] = React.useState(String(nilai.jumlah));
  const [alasan, setAlasan] = React.useState("");
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await apiKoreksiPengembalian(entri.id, kembali
        ? { tunai_baru: num(tunai), transfer_baru: num(transfer), alasan }
        : { jumlah_baru: num(jumlah), alasan });
      onDone();
    } catch (e2) { setError(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <form data-testid={`kor-kembali-form-${entri.id}`} onSubmit={submit} className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 items-end rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
      {kembali ? (<>
        <div><label className={lbl}>Tunai baru</label><input data-testid="kor-kembali-tunai" type="number" min="0" value={tunai} onChange={(e) => setTunai(e.target.value)} className={inputCls} /></div>
        <div><label className={lbl}>Transfer baru</label><input data-testid="kor-kembali-transfer" type="number" min="0" value={transfer} onChange={(e) => setTransfer(e.target.value)} className={inputCls} /></div>
      </>) : (
        <div className="col-span-2"><label className={lbl}>Jumlah baru</label><input data-testid="kor-kembali-jumlah" type="number" min="0" value={jumlah} onChange={(e) => setJumlah(e.target.value)} className={inputCls} /></div>
      )}
      <div className="col-span-2"><label className={lbl}>Alasan (wajib)</label><input data-testid="kor-kembali-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} required minLength={5} maxLength={300} className={inputCls} /></div>
      <button data-testid="kor-kembali-submit" type="submit" disabled={busy} className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">{busy ? "…" : "Simpan koreksi"}</button>
      {error && <p data-testid="kor-kembali-error" className="col-span-2 md:col-span-5 text-xs text-red-300">{error}</p>}
    </form>
  );
}

export default function PengembalianPanel({ notaId, rekening, tz, onChanged, onClose }) {
  const cetak = useCetak();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [korId, setKorId] = React.useState(null);
  const load = React.useCallback(async () => {
    try { setData(await apiGetPengembalian(notaId)); setError(null); } catch (e) { setError(errText(e)); }
  }, [notaId]);
  React.useEffect(() => { load(); }, [load]);
  const refresh = async () => { setKorId(null); await load(); onChanged(); };

  if (error) return <div data-testid="refund-error" className="text-red-300 text-sm">{error}</div>;
  if (!data) return <div className="p-4 text-slate-400"><Loader2 className="inline animate-spin mr-2" size={16} />Memuat…</div>;
  const p = data.pengembalian;
  // the latest value of each original entry, after its corrections
  const nilai = {};
  data.entri.forEach((e) => {
    if (e.jenis === "KOREKSI") nilai[e.pengembalian_id] = e.nilai_baru;
    else nilai[e.id] = { tunai: e.tunai || 0, transfer: e.transfer || 0, jumlah: e.jumlah };
  });
  return (
    <div data-testid="refund-detail" className="rounded-xl border border-red-500/30 bg-slate-900/60 p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <span data-testid="refund-nomor" className="text-xl font-mono text-amber-300">{data.nomor_nota}</span>
        <span data-testid="refund-status" className={`text-xs font-mono px-2 py-1 rounded border ${p.status === "SELESAI" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>{p.status}</span>
        <span className="text-sm text-slate-300">Dibayar saat batal <b>{rupiah(data.dibayar_saat_batal)}</b> · Dikembalikan <b data-testid="refund-dikembalikan">{rupiah(p.dikembalikan)}</b> · Tidak dikembalikan <b data-testid="refund-tidak">{rupiah(p.tidak_dikembalikan)}</b> · Sisa <b data-testid="refund-saldo" className="text-amber-300">{rupiah(p.saldo)}</b></span>
        {cetak.nota && <a href={`/nota/${notaId}/cetak`} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:border-amber-500/50"><Printer size={13} /> Cetak nota</a>}
        <button data-testid="refund-close" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
      </div>
      <p className="text-xs text-slate-400">Alasan batal: <span className="text-slate-200">{data.alasan_batal}</span></p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="refund-table">
          <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
            <th className="py-2 pr-3">Tanggal</th><th className="pr-3">Jenis</th><th className="pr-3 text-right">Tunai</th><th className="pr-3 text-right">Transfer</th>
            <th className="pr-3 text-right">Jumlah</th><th className="pr-3">Rekening</th><th className="pr-3">Oleh</th><th className="pr-3">Alasan / keterangan</th><th /></tr></thead>
          <tbody>
            {data.entri.length === 0 && <tr><td colSpan={9} className="py-4 text-center text-slate-500">Belum ada catatan pengembalian.</td></tr>}
            {data.entri.map((e) => (
              <React.Fragment key={e.id}>
                <tr data-testid={`refund-row-${e.id}`} className={`border-b border-slate-800/60 ${e.jenis === "KOREKSI" ? "text-amber-200/90" : ""}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{fmtTanggal(e.tanggal, tz)}</td>
                  <td className="pr-3 text-xs">{JENIS[e.jenis]}</td>
                  {e.jenis === "KOREKSI" ? (<>
                    <td className="pr-3 text-right font-mono text-xs">{rupiah(e.nilai_lama.tunai)} → {rupiah(e.nilai_baru.tunai)}</td>
                    <td className="pr-3 text-right font-mono text-xs">{rupiah(e.nilai_lama.transfer)} → {rupiah(e.nilai_baru.transfer)}</td>
                    <td className="pr-3 text-right font-mono text-xs">{e.jumlah > 0 ? "+" : ""}{rupiah(e.jumlah)}</td>
                    <td className="pr-3 text-xs">—</td>
                  </>) : (<>
                    <td className="pr-3 text-right font-mono">{rupiah(e.tunai)}</td>
                    <td className="pr-3 text-right font-mono">{rupiah(e.transfer)}</td>
                    <td className="pr-3 text-right font-mono">{rupiah(e.jumlah)}</td>
                    <td className="pr-3 text-xs" data-testid={`refund-rek-${e.id}`}>{e.rekening_toko ? <>dari {e.rekening_toko.bank} {e.rekening_toko.nomor}<br />ke {e.rekening_customer?.bank} {e.rekening_customer?.nomor} a.n. {e.rekening_customer?.atas_nama}</> : "—"}</td>
                  </>)}
                  <td className="pr-3 text-xs">{e.oleh?.nama || e.oleh?.username}</td>
                  <td className="pr-3 text-xs">{e.alasan || e.keterangan || ""}</td>
                  <td className="text-right">
                    {e.jenis !== "KOREKSI" && (
                      <button data-testid={`refund-kor-open-${e.id}`} onClick={() => setKorId(korId === e.id ? null : e.id)} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:text-amber-300"><Pencil size={12} /> Koreksi</button>
                    )}
                  </td>
                </tr>
                {korId === e.id && <tr><td colSpan={9}><KoreksiForm entri={e} nilai={nilai[e.id]} onDone={refresh} /></td></tr>}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {p.status === "MENUNGGU" ? (
        <div className="grid gap-3">
          <KembaliForm key={`k-${p.saldo}`} notaId={notaId} saldo={p.saldo} rekening={rekening} onDone={refresh} />
          <TidakForm key={`t-${p.saldo}`} notaId={notaId} saldo={p.saldo} onDone={refresh} />
        </div>
      ) : (
        <p data-testid="refund-selesai" className="text-sm text-emerald-300">Pengembalian sudah selesai. Perubahan hanya lewat koreksi (tercatat).</p>
      )}
    </div>
  );
}
